import { useMemo } from 'react';

import { Box, type SxProps, type Theme } from '@mui/material';

import { DragHandle } from '@modules/_shared/components/sortable-list/DragHandle';
import type { SortableTableRowProps } from '@modules/_shared/components/sortable-list/types';
import { getColumnStyle } from '@modules/_shared/components/sortable-list/types';
import { useSortableItem } from '@modules/_shared/components/sortable-list/useSortableItem';

const useRowSx = ({
  isDragOverlay = false,
  isDragging = false,
  isSelected = false,
  onClick,
  rowHover = true,
}: {
  isDragOverlay?: boolean;
  isDragging?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  rowHover?: boolean;
}): SxProps<Theme> =>
  useMemo(
    () => (theme: Theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: '12px',
      py: 1,
      height: 65,
      backgroundColor: isDragOverlay
        ? (theme.vars ?? theme).palette.background.paper
        : isSelected
        ? (theme.vars ?? theme).palette.primary.lighter
        : (theme.vars ?? theme).palette.background.paper,
      borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
      opacity: isDragging && !isDragOverlay ? 0.5 : 1,
      boxShadow: isDragOverlay ? '0 10px 20px rgba(0,0,0,0.2)' : 'none',
      cursor: onClick ? 'pointer' : 'default',
      ...(rowHover && {
        '&:hover': {
          backgroundColor: isDragOverlay
            ? (theme.vars ?? theme).palette.background.paper
            : isSelected
            ? (theme.vars ?? theme).palette.action.selected
            : (theme.vars ?? theme).palette.action.hover,
        },
      }),
    }),
    [isDragOverlay, isDragging, isSelected, onClick, rowHover],
  );

const TableRowColumns = <T extends { id: string }>({
  item,
  columns,
}: {
  item: T;
  columns: SortableTableRowProps<T>['columns'];
}) => {
  const visibleColumns = useMemo(
    () => columns.filter(col => !col.hide),
    [columns],
  );

  return (
    <>
      {visibleColumns.map(column => {
        const value = item[column.field as keyof T];
        const cellContent = column.renderCell
          ? column.renderCell({ row: item, value, field: column.field })
          : String(value ?? '');

        return (
          <Box
            key={column.field}
            sx={{
              ...getColumnStyle(column),
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cellContent}
          </Box>
        );
      })}
    </>
  );
};

/**
 * Plain table row without DnD context dependency.
 * Used when enableDragAndDrop is false.
 */
export const TableRow = <T extends { id: string }>({
  item,
  columns,
  isSelected = false,
  onClick,
  rowHover = true,
}: Pick<
  SortableTableRowProps<T>,
  'item' | 'columns' | 'isSelected' | 'onClick' | 'rowHover'
>) => {
  const sx = useRowSx({ isSelected, onClick, rowHover });

  return (
    <Box data-row-id={item.id} onClick={onClick} sx={sx}>
      <TableRowColumns item={item} columns={columns} />
    </Box>
  );
};

/**
 * Table row with DnD support. Must be rendered inside a SortableContext.
 */
export const SortableTableRow = <T extends { id: string }>({
  item,
  columns,
  isDragOverlay = false,
  isSelected = false,
  onClick,
  rowHover = true,
  dragHandleWidth = 32,
}: SortableTableRowProps<T>) => {
  const { setNodeRef, style, isDragging, dragHandleProps } = useSortableItem(
    item.id,
  );

  const sx = useRowSx({
    isDragOverlay,
    isDragging,
    isSelected,
    onClick,
    rowHover,
  });

  return (
    <Box
      data-row-id={item.id}
      ref={isDragOverlay ? undefined : setNodeRef}
      style={isDragOverlay ? undefined : style}
      onClick={onClick}
      sx={sx}
    >
      <Box
        sx={{
          width: dragHandleWidth,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <DragHandle
          attributes={dragHandleProps.attributes}
          listeners={dragHandleProps.listeners}
          isDragging={isDragging}
          variant="inline"
        />
      </Box>

      <TableRowColumns item={item} columns={columns} />
    </Box>
  );
};
